import React from 'react';
import { useNavigate } from 'react-router-dom';

interface VonSession {
  id: string;
  title: string;
  sessionType: string;
  totalMessages: number;
  lastActivityAt: string;
  createdAt: string;
  isArchived: boolean;
}

interface SessionsListProps {
  sessions: VonSession[];
  currentSession: VonSession | null;
  onSelectSession: (sessionId: string) => void;
  onCreateNewSession: () => void;
  isCreatingSession: boolean;
}

export const SessionsList: React.FC<SessionsListProps> = ({
  sessions,
  currentSession,
  onSelectSession,
  onCreateNewSession,
  isCreatingSession
}) => {
  const navigate = useNavigate();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const truncateTitle = (title: string, maxLength: number = 30) => {
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  return (
    <div className="bg-white border-r border-gray-200 w-80 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate('/')}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go to homepage"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10M9 21h6" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Von Chat</h2>
          </div>
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
            {sessions.length} session{sessions.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        <button
          onClick={onCreateNewSession}
          disabled={isCreatingSession}
          className={`w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${
            isCreatingSession ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isCreatingSession ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Creating...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Chat</span>
            </>
          )}
        </button>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="p-6 text-center">
            <div className="text-gray-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-sm text-gray-600 mb-4">No chat sessions yet</p>
            <p className="text-xs text-gray-500">
              Create your first Von Chat session to start exploring your data with AI
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => onSelectSession(session.id)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  currentSession?.id === session.id
                    ? 'bg-blue-50 border border-blue-200'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className={`text-sm font-medium truncate ${
                        currentSession?.id === session.id ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {truncateTitle(session.title)}
                      </h3>
                      {session.totalMessages > 0 && (
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          currentSession?.id === session.id
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {session.totalMessages}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs ${
                        currentSession?.id === session.id ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {formatDate(session.lastActivityAt)}
                      </span>
                      
                      {session.isArchived && (
                        <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                          Archived
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {currentSession?.id === session.id && (
                    <div className="ml-2 flex-shrink-0">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          <div className="flex items-center justify-center space-x-1 mb-1">
            <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
            <span>Enhanced AI chat with data integration</span>
          </div>
          <div>Neural search • Model selection • Smart filtering</div>
        </div>
      </div>
    </div>
  );
}; 